import { useRef, useEffect, useCallback, forwardRef } from 'react';
import { observer } from 'mobx-react-lite';
import styled, { css } from 'styled-components';

import { IconCloseRegular } from '@components/icons/close-regular';
import { IconSearch } from '@components/icons/search';
import { Input } from 'components/Input';

export const SearchBox = observer((props) => {
  const instanceRef = useRef(null);

  const { clear, onClose, onChange, isOpen, value, placeholder = 'Search' } = props;

  const onCloseClick = useCallback(() => {
    clear && clear();
    onClose && onClose();
  }, [clear, onClose]);

  const handleKeyPress = useCallback(
    (event) => {
      if (isOpen && event.keyCode === 27) {
        onCloseClick();
      }
    },
    [isOpen, onCloseClick]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    if (isOpen && instanceRef.current) {
      instanceRef.current.focus();
    }
  });

  return (
    <SearchContainer open={isOpen} onKeyPress={handleKeyPress}>
      <RelativeWrapper>
        <SearchBoxInput
          keepFocus
          ref={instanceRef}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
        {isOpen && (
          <CloseIconContainer onClick={onCloseClick}>
            <IconCloseRegular />
          </CloseIconContainer>
        )}
      </RelativeWrapper>
    </SearchContainer>
  );
});

const SearchInputBoxComponent = forwardRef((props, ref) => {
  return (
    <RelativeWrapper>
      <SearchIconContainer>
        <IconSearch />
      </SearchIconContainer>
      <SearchInput ref={ref} {...props} />
    </RelativeWrapper>
  );
});

export const SearchBoxInput = observer(SearchInputBoxComponent);

const SearchInput = styled(Input)((p) => ({
  transition: 'all 150ms ease-out',
  paddingLeft: '2.25rem',
  backgroundColor: p.keepFocus ? p.theme.colors.white100 : p.theme.colors.purpleTrans5,
  border: '1px solid ' + (p.keepFocus ? p.theme.colors.purple100 : 'transparent'),
  ...p.theme.regular16,
  ...(!p.keepFocus && {
    ':focus': {
      backgroundColor: p.theme.colors.white100,
      borderColor: p.theme.colors.purple100,
    },
  }),
}));

const RelativeWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIconContainer = styled.div`
  position: absolute;
  color: ${(p) => p.theme.colors.purple60};
  top: calc(50% - 0.75rem);
  height: 1.5rem;
  width: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  left: 0.5rem;
  z-index: 2;
`;

const CloseIconContainer = styled.div`
  position: absolute;
  :hover {
    cursor: pointer;
    color: ${(p) => p.theme.colors.purple80};
  }
  right: 0.5rem;
  top: calc(50% - 0.75rem);
  height: 1.5rem;
  width: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(p) => p.theme.colors.purple50};
`;

const SearchContainer = styled.div`
  position: absolute;
  color: ${(p) => p.theme.colors.purple60};
  right: 0;
  height: 2.5rem;
  min-width: 17.5rem;
  display: flex;
  align-items: center;
  background-color: #fff;
  width: 0;
  opacity: 0;
  z-index: 0;

  ${(p) =>
    p.open &&
    css`
      z-index: 2;
      width: 100%;
      opacity: 1;
      transition: opacity 300ms ease;
    `};
`;
